import { useRef, useState } from "react";
import { modulesByLevel, type Course } from "@/core/types";
import { Icon } from "@/ui/components/Icon";
import { CurriculumBand, StatusRing, moduleSteps } from "@/ui/components/Progress";
import {
  courseStats,
  findNextLesson,
  formatMinutes,
  isLessonDone,
  lessonStepsDone,
  lookupFor,
  moduleMinutes,
} from "@/ui/progressStats";
import { Breadcrumbs, NextLessonCard } from "./parts";
import type { AllProgress } from "./useAllProgress";
import { href } from "./useHashRoute";

interface WebCourseProps {
  course: Course;
  progress: AllProgress;
}

export function WebCourse({ course, progress }: WebCourseProps) {
  const saved = progress.get(course.id);
  const lookup = lookupFor(saved);
  const stats = courseStats(course, lookup);
  const next = findNextLesson(course, lookup, saved.lastLessonKey);
  const groups = modulesByLevel(course);
  const tasks = course.modules
    .flatMap((module) => module.lessons)
    .flatMap((lesson) => lesson.steps)
    .filter((step) => step.task).length;

  // Aufgeklappt ist zu Beginn das Modul der nächsten Lektion.
  const [openModules, setOpenModules] = useState<Set<string>>(
    () => new Set(next ? [next.module.id] : []),
  );
  const moduleRefs = useRef(new Map<string, HTMLLIElement>());

  const toggle = (moduleId: string) =>
    setOpenModules((current) => {
      const updated = new Set(current);
      if (updated.has(moduleId)) updated.delete(moduleId);
      else updated.add(moduleId);
      return updated;
    });

  const reveal = (moduleId: string) => {
    setOpenModules((current) => new Set(current).add(moduleId));
    requestAnimationFrame(() =>
      moduleRefs.current.get(moduleId)?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  return (
    <div className="w-page w-course-page" data-host={course.host}>
      <Breadcrumbs items={[{ label: "Übersicht", to: href.home() }, { label: course.title }]} />

      <header className="w-title-row">
        <div>
          <h1>{course.title}</h1>
          <p className="w-title-row__sub">{course.summary}</p>
        </div>
      </header>

      <div className="w-grid">
        <dl className="w-strip">
          <div>
            <dt>Fortschritt</dt>
            <dd className="num">
              {stats.percentage}
              <small> %</small>
            </dd>
            <dd className="w-strip__caption">der Schritte erledigt</dd>
          </div>
          <div>
            <dt>Schritte</dt>
            <dd className="num">{stats.stepsDone}</dd>
            <dd className="w-strip__caption num">von {stats.stepsTotal}</dd>
          </div>
          <div>
            <dt>Lektionen</dt>
            <dd className="num">{stats.lessonsDone}</dd>
            <dd className="w-strip__caption num">von {stats.lessonsTotal}</dd>
          </div>
          <div>
            <dt>Lernzeit</dt>
            <dd className="num">{formatMinutes(stats.minutesDone)}</dd>
            <dd className="w-strip__caption num">von {formatMinutes(stats.minutesTotal)}</dd>
          </div>
        </dl>

        <section className="w-card w-plan" aria-labelledby="lehrplan-titel">
          <header className="w-panel__head">
            <h2 className="w-panel__title" id="lehrplan-titel">
              Lehrplan
            </h2>
            <span className="w-panel__meta">Ein Segment pro Modul · zeigen für Details</span>
          </header>
          <CurriculumBand course={course} progress={lookup} onSelectModule={reveal} />
        </section>

        <div className="w-modules">
          {groups.map((group) => (
            <section key={group.level} className="w-level" aria-labelledby={`stufe-${group.level}`}>
              <header className="w-section-head">
                <h2 id={`stufe-${group.level}`}>{group.level}</h2>
                <span className="w-panel__meta">
                  {group.modules.length} Module ·{" "}
                  {formatMinutes(group.modules.reduce((sum, m) => sum + moduleMinutes(m), 0))}
                </span>
              </header>
              <ul className="w-card w-module-list">
                {group.modules.map((module) => {
                  const steps = moduleSteps(module, lookup);
                  const lessonsDone = module.lessons.filter((lesson) =>
                    isLessonDone(module, lesson, lookup),
                  ).length;
                  const isOpen = openModules.has(module.id);
                  const number = course.modules.indexOf(module) + 1;

                  return (
                    <li
                      key={module.id}
                      className={`w-module ${isOpen ? "is-open" : ""}`}
                      ref={(element) => {
                        if (element) moduleRefs.current.set(module.id, element);
                        else moduleRefs.current.delete(module.id);
                      }}
                    >
                      <button
                        type="button"
                        className="w-module__head"
                        aria-expanded={isOpen}
                        onClick={() => toggle(module.id)}
                      >
                        <StatusRing fraction={steps.done / steps.total} />
                        <span className="w-module__text">
                          <span className="w-module__title">{module.title}</span>
                          <span className="w-module__meta num">
                            Modul {number} · {lessonsDone} von {module.lessons.length} Lektionen ·{" "}
                            {formatMinutes(moduleMinutes(module))}
                          </span>
                        </span>
                        <span className="w-module__figure num">
                          <span>
                            {steps.done}
                            <small>/{steps.total}</small>
                          </span>
                          <span className="w-meter" aria-hidden="true">
                            <span
                              className="w-meter__fill"
                              style={{ transform: `scaleX(${steps.done / steps.total})` }}
                            />
                          </span>
                        </span>
                        <Icon name="chevronDown" className="w-module__chevron" />
                      </button>

                      {isOpen ? (
                        <div className="w-module__body">
                          <p className="w-module__summary">{module.summary}</p>
                          <ol className="w-lessons">
                            {module.lessons.map((lesson) => {
                              const done = lessonStepsDone(module, lesson, lookup);
                              const isNext =
                                next?.module.id === module.id && next.lesson.id === lesson.id;
                              return (
                                <li key={lesson.id}>
                                  <a
                                    className={`w-lesson ${isNext ? "is-next" : ""}`}
                                    href={href.lesson(course.id, module.id, lesson.id)}
                                  >
                                    <StatusRing fraction={done / lesson.steps.length} size={16} />
                                    <span className="w-lesson__title">{lesson.title}</span>
                                    {isNext ? <span className="pill">Als Nächstes</span> : null}
                                    <span className="w-lesson__steps num">
                                      {done}/{lesson.steps.length} Schritte
                                    </span>
                                    <span className="w-lesson__time num">
                                      {lesson.durationMinutes} Min.
                                    </span>
                                  </a>
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <aside className="w-rail">
          <NextLessonCard course={course} next={next} lookup={lookup} />

          <section className="w-panel" aria-labelledby="kurs-daten">
            <h2 className="w-panel__title" id="kurs-daten">
              Über diesen Kurs
            </h2>
            <dl className="w-facts num">
              {groups.map((group) => (
                <div key={group.level}>
                  <dt>{group.level}</dt>
                  <dd>{group.modules.length} Module</dd>
                </div>
              ))}
              <div>
                <dt>Lektionen</dt>
                <dd>{stats.lessonsTotal}</dd>
              </div>
              <div>
                <dt>Schritte mit Aufgabe</dt>
                <dd>
                  {tasks} von {stats.stepsTotal}
                </dd>
              </div>
              <div>
                <dt>Dauer</dt>
                <dd>{formatMinutes(stats.minutesTotal)}</dd>
              </div>
            </dl>
            <p className="w-panel__note">
              Die Aufgaben prüft das Add-in direkt im Dokument – im Seitenpanel von {course.host}.
            </p>
          </section>

          {stats.stepsDone > 0 ? (
            <button
              type="button"
              className="text-button text-button--danger"
              onClick={() => {
                if (
                  window.confirm(
                    `Fortschritt im ${course.title} zurücksetzen? ` +
                      `${stats.stepsDone} erledigte Schritte in diesem Browser gehen verloren.`,
                  )
                ) {
                  progress.reset(course.id);
                }
              }}
            >
              <Icon name="reset" />
              Fortschritt zurücksetzen
            </button>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
